import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  createProduct,
  getProductImageStagingUploadUrl,
} from '@vn-propane/shared/client';
import type {
  CreateProductRequest,
  ErrorResponse,
  UserOrganization,
} from '@vn-propane/shared/client';
import { ArrowLeft, ImageIcon, Loader2, Star, Trash2, Upload } from 'lucide-react';
import { getPriceInputStep, getPricePlaceholder } from '@/lib/utils';

interface OrganizationContext {
  organization: UserOrganization;
}

/// A staged image is uploaded to S3 immediately but not yet attached to any
/// product. The attachment happens server-side inside the product-create
/// transaction. The preview URL is a browser-local object URL so we don't
/// need a confirmed file_uploads row to render it.
type StagedImage = {
  uploadId: string;
  previewUrl: string;
  filename: string;
};

export default function NewProduct() {
  const { id: orgId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const { organization } = useOutletContext<OrganizationContext>();
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [formData, setFormData] = useState<Omit<CreateProductRequest, 'imageUploadIds'>>({
    name: '',
    sku: null,
    unit: '',
    price: null,
  });

  const [stagedImages, setStagedImages] = useState<StagedImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Revoke any object URLs we created when unmounting so we don't leak.
  useEffect(() => {
    return () => {
      stagedImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImagePick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !orgId) return;

    if (!file.type.startsWith('image/')) {
      setImageError(t('products.imageMustBeImage'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError(t('products.imageTooLarge'));
      return;
    }

    setUploading(true);
    setImageError(null);
    try {
      const uploadUrlResult = await getProductImageStagingUploadUrl({
        path: { org_id: orgId },
        body: {
          fileType: 'product_image',
          // referenceId is required by the schema but unused for staging
          // uploads — the product_id gets stamped at create time.
          referenceId: '',
          contentType: file.type,
          filename: file.name,
          fileSizeBytes: file.size,
        },
      });

      if (uploadUrlResult.error) {
        const err = uploadUrlResult.error as ErrorResponse;
        throw new Error(err.error?.message || t('products.imageUploadFailed'));
      }
      const { uploadId, uploadUrl } = uploadUrlResult.data!.data;

      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type, 'x-amz-acl': 'public-read' },
        body: file,
      });
      if (!s3Response.ok) {
        throw new Error(t('products.imageUploadFailed'));
      }

      const previewUrl = URL.createObjectURL(file);
      setStagedImages((prev) => [
        ...prev,
        { uploadId, previewUrl, filename: file.name },
      ]);
    } catch (err) {
      setImageError(err instanceof Error ? err.message : t('products.imageUploadFailed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeStagedImage = (uploadId: string) => {
    setStagedImages((prev) => {
      const next = prev.filter((img) => img.uploadId !== uploadId);
      const removed = prev.find((img) => img.uploadId === uploadId);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
    // We deliberately don't call a server-side delete here — the file_uploads
    // row is still in 'pending' state and will be reaped by the expired
    // upload sweeper. Calling delete now would require either an extra
    // endpoint or a race-prone fire-and-forget that's not worth the complexity.
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !formData.name || !formData.unit) return;

    setSaving(true);
    setSubmitError(null);
    const result = await createProduct({
      path: { org_id: orgId },
      body: {
        ...formData,
        imageUploadIds: stagedImages.map((img) => img.uploadId),
      },
    });

    if (result.error) {
      const err = result.error as ErrorResponse;
      setSubmitError(err.error?.message || t('products.failedToLoadProduct'));
      setSaving(false);
      return;
    }

    if (result.data) {
      // Release preview URLs before we navigate away.
      stagedImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      navigate(`/organizations/${orgId}/products/${result.data.data.id}`);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <Link
          to={`/organizations/${orgId}/products`}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('products.backToProducts')}
        </Link>
      </div>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{t('products.addProduct')}</h1>
          <p className="text-muted-foreground">
            {t('products.addNewProduct')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('products.productNameRequired')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sku">{t('products.sku')}</Label>
              <Input
                id="sku"
                value={formData.sku || ''}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value || null })}
                placeholder="PROP-12KG"
              />
              <p className="text-xs text-muted-foreground">
                {t('products.skuDescription')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit">{t('products.unitOfMeasureRequired')}</Label>
              <Input
                id="unit"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder={t('products.unitPlaceholder')}
              />
              <p className="text-xs text-muted-foreground">
                {t('products.unitDescription')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">{t('products.price')} ({organization.currency})</Label>
              <Input
                id="price"
                type="number"
                step={getPriceInputStep(organization.currency)}
                min="0"
                value={formData.price || ''}
                onChange={(e) => setFormData({ ...formData, price: e.target.value || null })}
                placeholder={getPricePlaceholder(organization.currency)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('products.images')}</Label>
            <p className="text-sm text-muted-foreground">
              {t('products.imagesDescription')}
            </p>

            {stagedImages.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {stagedImages.map((img, idx) => (
                  <div
                    key={img.uploadId}
                    className="relative border rounded-lg overflow-hidden bg-muted aspect-square group"
                  >
                    <img
                      src={img.previewUrl}
                      alt={img.filename}
                      className="w-full h-full object-contain"
                    />
                    {idx === 0 && (
                      <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-primary text-primary-foreground">
                        <Star className="w-3 h-3 fill-current" />
                        {t('products.primary')}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeStagedImage(img.uploadId)}
                      className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90"
                      title={t('products.removeImage')}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {stagedImages.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground italic">
                <ImageIcon className="w-4 h-4" />
                {t('products.noImagesYet')}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImagePick}
              className="hidden"
              id="new-product-image-upload"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !orgId}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {t('products.uploadImage')}
            </Button>
            {imageError && <p className="text-xs text-destructive">{imageError}</p>}
          </div>

          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}

          <div className="flex gap-4">
            <Button type="submit" disabled={!formData.name || !formData.unit || saving || uploading}>
              {saving ? t('products.creating') : t('products.createProduct')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/organizations/${orgId}/products`)}
            >
              {t('common:buttons.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
